import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import StatsCard from './StatsCard';
import RecentSales from './RecentSales';
import TopProducts from './TopProducts';
import CashierDashboard from './CashierDashboard';
import '../../styles/dashboard.css';

const Dashboard = () => {
  const { hasPermission, hasModule } = useAuth();
  const [stats, setStats] = useState({
    todaySales: 0,
    todayCount: 0,
    weeklySales: 0,
    weeklyCount: 0,
    monthlySales: 0,
    monthlyCount: 0,
    totalCustomers: 0,
    lowStockCount: 0,
    outstandingDebt: 0,
    todayTrend: 0,
    weeklyTrend: 0,
    monthlyTrend: 0
  });

  const [salesData, setSalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');

  useEffect(() => {
    if (hasPermission('view_reports')) {
      loadBranches();
      loadDashboardData();
    }
  }, [hasPermission, selectedBranch]);

  const loadBranches = async () => {
    try {
      const response = await api.get('/branches.php');
      setBranches(response.data.branches || []);
    } catch (err) {
      console.error('Failed to load branches:', err);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const params = selectedBranch !== 'all' ? { branch_id: selectedBranch } : {};
      
      const [statsRes, salesRes, categoryRes] = await Promise.all([
        api.get('/reports.php/dashboard', { params }),
        api.get('/reports.php/sales-trend', { params: { days: 7, ...params } }),
        api.get('/reports.php/sales-by-category', { params: { days: 30, ...params } })
      ]);

      console.log('API Responses:', { 
        stats: statsRes.data, 
        sales: salesRes.data, 
        category: categoryRes.data 
      });

      const data = statsRes.data;
      setStats({
        todaySales: data.today_sales?.total || 0,
        todayCount: data.today_sales?.count || 0,
        weeklySales: data.weekly_sales?.total || 0,
        weeklyCount: data.weekly_sales?.count || 0,
        monthlySales: data.monthly_sales?.total || 0,
        monthlyCount: data.monthly_sales?.count || 0,
        totalCustomers: data.total_customers || 0,
        lowStockCount: data.low_stock_count || 0,
        outstandingDebt: data.outstanding_debt || 0,
        todayTrend: 0,
        weeklyTrend: 0,
        monthlyTrend: 0
      });

      // Ensure salesData is an array
      setSalesData(Array.isArray(salesRes.data) ? salesRes.data : []);

      // Ensure categoryData is an array - FIX THE ERROR HERE
      const categoryArray = Array.isArray(categoryRes.data) 
        ? categoryRes.data 
        : (categoryRes.data?.categories || categoryRes.data?.data || []);
      
      setCategoryData(categoryArray);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'KSh 0';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount).replace('KES', 'KSh');
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B6B', '#4ECDC4', '#45B7D1'];

  // Prepare data for pie chart - ensure it has the right format
  const pieChartData = categoryData.map(item => ({
    name: item.name || item.category_name || item.label || 'Unknown',
    value: Number(item.value || item.sales || item.total || 0)
  })).filter(item => item.value > 0);

  if (!hasPermission('view_reports')) {
    return <CashierDashboard />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="dashboard-controls" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select 
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
          >
            <option value="all">All Branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <div className="date-range">
            <span>Last 30 days</span>
          </div>
          {hasModule('ai_bi') && hasPermission('view_reports') && (
            <Link to="/ai" style={{ marginLeft: '0.75rem', color: '#2C9BD9', fontWeight: 600 }}>
              AI Insights →
            </Link>
          )}
        </div>
      </div>

      <div className="stats-grid">
        {hasModule('pos') && (
          <>
            <StatsCard
              title="Today's Sales"
              value={formatCurrency(stats.todaySales)}
              subtitle={`${stats.todayCount || 0} transactions`}
              icon="💰"
              trend={stats.todayTrend}
            />
            <StatsCard
              title="This Week"
              value={formatCurrency(stats.weeklySales)}
              subtitle={`${stats.weeklyCount || 0} transactions`}
              icon="📊"
              trend={stats.weeklyTrend}
            />
            <StatsCard
              title="This Month"
              value={formatCurrency(stats.monthlySales)}
              subtitle={`${stats.monthlyCount || 0} transactions`}
              icon="📈"
              trend={stats.monthlyTrend}
            />
          </>
        )}
        {hasModule('crm') && (
          <StatsCard
            title="Customers"
            value={stats.totalCustomers || 0}
            icon="👥"
          />
        )}
        {hasModule('inventory') && (
          <StatsCard
            title="Low Stock Items"
            value={stats.lowStockCount || 0}
            icon="⚠️"
            alert={stats.lowStockCount > 0}
          />
        )}
        {hasModule('finance') && (
          <StatsCard
            title="Outstanding Debt"
            value={formatCurrency(stats.outstandingDebt)}
            icon="💳"
            alert={stats.outstandingDebt > 0}
          />
        )}
      </div>

      {hasModule('pos') && (
      <div className="dashboard-row">
        <div className="dashboard-col-8">
          <div className="chart-card">
            <h3>Sales Trend (Last 7 Days)</h3>
            {salesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#2C9BD9" 
                    name="Sales"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="transactions" 
                    stroke="#132248" 
                    name="Transactions"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">No sales data available</div>
            )}
          </div>
        </div>

        <div className="dashboard-col-4">
          <div className="chart-card">
            <h3>Sales by Category</h3>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                    outerRadius={80}
                    fill="#2C9BD9"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">No category data available</div>
            )}
          </div>
        </div>
      </div>
      )}

      {hasModule('pos') && (
      <div className="dashboard-row">
        <div className="dashboard-col-6">
          <TopProducts />
        </div>
        <div className="dashboard-col-6">
          <RecentSales />
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard;